import type { PluginTierList } from "@glide/plugins";
import type { EminenceFlags } from "@glide/billing-types";
import { PluginAccessOverride } from "@glide/billing-types";
import { getFeatureSetting } from "./feature-settings";
import { isTemplate } from "./database-strings";

/**
 * Checks whether a plugin access should be overridden by allow/deny lists.
 * The precedence between allow and deny lists is controlled by the
 * pluginAllowListTakesPrecedence feature flag.
 *
 * @returns "allow" if plugin should be allowed, "deny" if denied, "no-override" if no override applies
 */
export function checkPluginAccessOverride(
    pluginId: string,
    pluginAllowList: string[],
    pluginDenyList: string[]
): PluginAccessOverride {
    const allowListTakesPrecedence = getFeatureSetting("pluginAllowListTakesPrecedence");

    if (allowListTakesPrecedence) {
        if (pluginAllowList.includes(pluginId)) return PluginAccessOverride.Allow;
        if (pluginDenyList.includes(pluginId)) return PluginAccessOverride.Deny;
    } else {
        if (pluginDenyList.includes(pluginId)) return PluginAccessOverride.Deny;
        if (pluginAllowList.includes(pluginId)) return PluginAccessOverride.Allow;
    }

    return PluginAccessOverride.NoOverride;
}

/**
 * This is the canonical way of checking whether the given plugin is supported
 * by the given eminence flags.
 */
export function hasRequiredEntitlementsForPlugin(
    appID: string | undefined,
    pluginId: string,
    pluginTiers: PluginTierList | undefined,
    eminenceFlags: EminenceFlags
): boolean {
    const unifiedPluginEminenceChecks = getFeatureSetting("unifiedPluginEminenceChecks");

    if (unifiedPluginEminenceChecks && appID !== undefined && isTemplate(appID)) {
        return true;
    }

    if (pluginTiers === undefined) {
        return true;
    }

    const override = checkPluginAccessOverride(pluginId, eminenceFlags.pluginAllowList, eminenceFlags.pluginDenyList);
    if (override === PluginAccessOverride.Deny) {
        return false;
    }
    if (override === PluginAccessOverride.Allow) {
        return true;
    }

    if (unifiedPluginEminenceChecks && eminenceFlags.allowedPlugins.includes(pluginId)) {
        return true;
    }

    return pluginTiers.includes(eminenceFlags.pluginTier);
}
